import React, { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import NotFound from 'components/404';
import Loader from 'wrapper/loader';
import TenantGate from 'wrapper/tenant-gate';

const SessionExpire = React.lazy(() => import('wrapper/session-expire/SessionExpire'));
const InviteExpire = React.lazy(() => import('wrapper/invite-expire'));
const EmailVerified = React.lazy(() => import('wrapper/email-verified'));
const VerifyPassword = React.lazy(() => import('pages/verify-password'));
const StytchCallback = React.lazy(() => import('pages/auth/StytchCallback'));
const VerifyEmail = React.lazy(() => import('wrapper/verify-email'));
const PersonalInfoCard = React.lazy(() => import('wrapper/personal-info'));
const UnifiedLogin = React.lazy(() => import('pages/auth/UnifiedLogin'));
const LoginPrompt = React.lazy(() => import('wrapper/login-page'));

const authRoutes = [
  // ── Tenant-gated routes ──────────────────────────────────────────
  // These validate org/branch slug from the hostname before rendering.
  {
    element: <TenantGate />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <LoginPrompt />
          </Suspense>
        ),
      },
      // Primary B2B Stytch login page (email + magic link)
      {
        path: '/login/b2b',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <UnifiedLogin initialUserType="specialist" />
          </Suspense>
        ),
      },
      // B2C consumer magic-link login page
      {
        path: '/login/consumer',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <UnifiedLogin initialUserType="buyer" />
          </Suspense>
        ),
      },
      // Backwards-compatible entrypoint for direct testing
      {
        path: '/stytch-login',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <UnifiedLogin initialUserType="specialist" />
          </Suspense>
        ),
      },
      {
        path: '/login',
        element: <Navigate to="/login/consumer" replace />,
      },
    ],
  },

  // ── Ungated routes ───────────────────────────────────────────────
  // Callbacks and legacy flows must work regardless of tenant validation.

  // Stytch magic-link callback handler
  {
    path: '/auth/callback',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <StytchCallback />
      </Suspense>
    ),
  },
  // Branch portal callback (B2C shim redirects here)
  {
    path: '/branch/auth/callback',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <StytchCallback />
      </Suspense>
    ),
  },
  // Legacy / existing flows
  {
    path: '/setup-password',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <VerifyPassword />
      </Suspense>
    ),
  },
  {
    path: '/session-timeout',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <SessionExpire />
      </Suspense>
    ),
  },
  {
    path: '/verified',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <InviteExpire />
      </Suspense>
    ),
  },
  {
    path: '/result_page',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <EmailVerified />
      </Suspense>
    ),
  },
  {
    path: '/personal-info',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <PersonalInfoCard />
      </Suspense>
    ),
  },
  {
    path: '/verification-email',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <VerifyEmail />
      </Suspense>
    ),
  },
  {
    path: '/auth0-callback-v2/verification-email',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <VerifyEmail />
      </Suspense>
    ),
  },
  {
    path: '/auth0-callback/verification-email',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <VerifyEmail />
      </Suspense>
    ),
  },
  {
    path: '/auth0-callback-v2/personal-info',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <PersonalInfoCard />
      </Suspense>
    ),
  },
  {
    path: '/auth0-callback/personal-info',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <PersonalInfoCard />
      </Suspense>
    ),
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default authRoutes;
